import { Grid, InputAdornment } from "@mui/material";
import Box from "@mui/material/Box";
import { ProjectLineItem } from "@interfaces/project";
import * as yup from "yup";
import { useFormik } from "formik";
import { useEffect } from "react";
import { NumericFormatCustom } from "@stories/atoms/NumericFormatCustom/NumericFormatCustom";
import { StyledTextField } from "@stories/atoms/StyledTextField/StyledTextField";
import { StyledButton } from "@stories/atoms/StyledButton/StyledButton";
import { StyledCard } from "@stories/atoms/StyledCard/StyledCard";
import { SubTitle } from "@stories/atoms/SubTitle/SubTitle";
import {
  calculatedAvailableSpend,
  calcRequiredDeemedHours,
  calcRequiredSpend,
} from "@utils/calc";

interface MPSGFormProps {
  project: ProjectLineItem;
  setProject: (project: ProjectLineItem) => void;
  handleBack: () => void;
  handleNext: () => void;
}

export const MPSGForm = ({
  project,
  setProject,
  handleNext,
  handleBack,
}: MPSGFormProps) => {
  const validationSchema = yup.object({
    totalProjectValue: yup.number().min(0).required(),
    projectMargin: yup
      .number()
      .min(0)
      .required()
      .test(
        "Less than project total",
        "Margin should be less than total project value",
        function (item) {
          return this.parent.totalProjectValue > item ? true : false;
        }
      ),
    specialisedEquipmentValue: yup.number().min(0),
    requiredHours: yup.number().min(0),
    deemedLabourRatio: yup.number().min(0),
    deemedLabourHourlyRate: yup.number().min(0),
    deemedRequiredHours: yup.number().min(0),
    mpsgHoursRequirement: yup.number().min(0),
  });

  const formik = useFormik({
    initialValues: {
      totalProjectValue: project.totalProjectValue,
      projectMargin: project.projectMargin,
      specialisedEquipmentValue: project.specialisedEquipmentValue,
      adjustedMpsgValue: calculatedAvailableSpend(
        project.totalProjectValue,
        project.projectMargin,
        0,
        project.specialisedEquipmentValue
      ),
      requiredHours: calcRequiredDeemedHours(
        calculatedAvailableSpend(
          project.totalProjectValue,
          project.projectMargin,
          0,
          project.specialisedEquipmentValue
        ),
        project.deemedLabourRatio,
        project.deemedLabourHourlyRate
      ),
      deemedLabourRatio: project.deemedLabourRatio,
      deemedLabourHourlyRate: project.deemedLabourHourlyRate,
      requiredSpend: project.totalProjectValue - project.projectMargin,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setProject({
        ...project,
        totalProjectValue: values.totalProjectValue,
        projectMargin: values.projectMargin,
        specialisedEquipmentValue: values.specialisedEquipmentValue,

        deemedLabourRatio: values.deemedLabourRatio,
        deemedLabourHourlyRate: values.deemedLabourHourlyRate,

        requiredHours: values.requiredHours,
        requiredSpend: values.requiredSpend,
      });
      handleNext();
    },
  });

  useEffect(() => {
    if (
      formik.values.totalProjectValue > 0 &&
      formik.values.projectMargin > 0
    ) {
      const adjustedMpsgValue = calculatedAvailableSpend(
        formik.values.totalProjectValue,
        formik.values.projectMargin,
        0,
        formik.values.specialisedEquipmentValue
      );
      formik.setFieldValue(
        "adjustedMpsgValue",
        parseFloat(adjustedMpsgValue.toFixed(2))
      );
    }
  }, [
    formik.values.totalProjectValue,
    formik.values.projectMargin,
    formik.values.specialisedEquipmentValue,
  ]);

  useEffect(() => {
    if (
      formik.values.adjustedMpsgValue > 0 &&
      formik.values.deemedLabourRatio > 0
    ) {
      const requiredSpend = calcRequiredSpend(
        formik.values.deemedLabourRatio,
        formik.values.adjustedMpsgValue
      );

      formik.setFieldValue(
        "requiredSpend",
        parseFloat(requiredSpend.toFixed(2))
      );
    }

    if (
      formik.values.adjustedMpsgValue > 0 &&
      formik.values.deemedLabourRatio > 0 &&
      formik.values.deemedLabourHourlyRate > 0
    ) {
      const requiredHours = calcRequiredDeemedHours(
        formik.values.adjustedMpsgValue,
        formik.values.deemedLabourRatio,
        formik.values.deemedLabourHourlyRate
      );
      formik.setFieldValue(
        "requiredHours",
        parseFloat(requiredHours.toFixed(2))
      );
    }
  }, [
    formik.values.adjustedMpsgValue,
    formik.values.deemedLabourRatio,
    formik.values.deemedLabourHourlyRate,
  ]);

  return (
    <StyledCard>
      <Grid container spacing={2} sx={{ padding: "5%" }}>
        <Grid item xs={12} md={12}>
          <SubTitle title={"Deemed Hours"} />
        </Grid>
        <Grid item xs={12} md={12}>
          <Box>
            <form onSubmit={formik.handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={2} sm={2}>
                  <StyledTextField
                    required
                    id="totalProjectValue"
                    name="totalProjectValue"
                    label="Total Project Value (ex. GST)"
                    fullWidth
                    margin="dense"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.totalProjectValue}
                    error={
                      formik.touched.totalProjectValue &&
                      Boolean(formik.errors.totalProjectValue)
                    }
                    helperText={
                      formik.touched.totalProjectValue
                        ? formik.errors.totalProjectValue
                        : ""
                    }
                    InputProps={{
                      inputComponent: NumericFormatCustom as any,
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={2} sm={2}>
                  <StyledTextField
                    required
                    id="projectMargin"
                    name="projectMargin"
                    label="Margin"
                    fullWidth
                    margin="dense"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.projectMargin}
                    error={
                      formik.touched.projectMargin &&
                      Boolean(formik.errors.projectMargin)
                    }
                    helperText={
                      formik.touched.projectMargin
                        ? formik.errors.projectMargin
                        : ""
                    }
                    InputProps={{
                      inputComponent: NumericFormatCustom as any,
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={2} sm={2}>
                  <StyledTextField
                    id="specialistEquipment"
                    name="specialistEquipment"
                    label="Less Specialist Equipment"
                    fullWidth
                    margin="dense"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.specialisedEquipmentValue}
                    error={
                      formik.touched.specialisedEquipmentValue &&
                      Boolean(formik.errors.specialisedEquipmentValue)
                    }
                    helperText={
                      formik.touched.specialisedEquipmentValue
                        ? formik.errors.specialisedEquipmentValue
                        : ""
                    }
                    InputProps={{
                      inputComponent: NumericFormatCustom as any,
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={6} sm={6}>
                  <StyledTextField
                    id="adjustedMpsgValue"
                    name="adjustedMpsgValue"
                    label="Less Margin"
                    fullWidth
                    margin="dense"
                    disabled={true}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.adjustedMpsgValue}
                    error={
                      formik.touched.adjustedMpsgValue &&
                      Boolean(formik.errors.adjustedMpsgValue)
                    }
                    helperText={
                      formik.touched.adjustedMpsgValue
                        ? formik.errors.adjustedMpsgValue
                        : ""
                    }
                    InputProps={{
                      inputComponent: NumericFormatCustom as any,
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={4} sm={4}>
                  <StyledTextField
                    required
                    id="deemedLabourRatio"
                    name="deemedLabourRatio"
                    label="Deemed Labour Ratio"
                    fullWidth
                    margin="dense"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.deemedLabourRatio}
                    error={
                      formik.touched.deemedLabourRatio &&
                      Boolean(formik.errors.deemedLabourRatio)
                    }
                    helperText={
                      formik.touched.deemedLabourRatio
                        ? formik.errors.deemedLabourRatio
                        : ""
                    }
                    type="number"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={8} sm={8}>
                  <StyledTextField
                    id="requiredSpend"
                    name="requiredSpend"
                    label="Required Spend"
                    fullWidth
                    margin="dense"
                    disabled={true}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.requiredSpend}
                    error={
                      formik.touched.requiredSpend &&
                      Boolean(formik.errors.requiredSpend)
                    }
                    helperText={
                      formik.touched.requiredSpend
                        ? formik.errors.requiredSpend
                        : ""
                    }
                    InputProps={{
                      inputComponent: NumericFormatCustom as any,
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={4} sm={4}>
                  <StyledTextField
                    required
                    id="deemedLabourHourlyRate"
                    name="deemedLabourHourlyRate"
                    label="Deemed Hourly Rate"
                    fullWidth
                    margin="dense"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.deemedLabourHourlyRate}
                    error={
                      formik.touched.deemedLabourHourlyRate &&
                      Boolean(formik.errors.deemedLabourHourlyRate)
                    }
                    helperText={
                      formik.touched.deemedLabourHourlyRate
                        ? formik.errors.deemedLabourHourlyRate
                        : ""
                    }
                    InputProps={{
                      inputComponent: NumericFormatCustom as any,
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={8} sm={8}>
                  <StyledTextField
                    id="requiredHours"
                    name="requiredHours"
                    label="Required Hours"
                    fullWidth
                    margin="dense"
                    disabled={true}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.requiredHours}
                    error={
                      formik.touched.requiredHours &&
                      Boolean(formik.errors.requiredHours)
                    }
                    helperText={
                      formik.touched.requiredHours
                        ? formik.errors.requiredHours
                        : ""
                    }
                    InputProps={{
                      inputComponent: NumericFormatCustom as any,
                      endAdornment: (
                        <InputAdornment position="start">hours</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid
                  item
                  xs={6}
                  sm={6}
                  sx={{
                    display: "flex !important",
                    justifyContent: "left !important;",
                  }}
                >
                  <StyledButton variant="contained" onClick={handleBack}>
                    Back
                  </StyledButton>
                </Grid>
                <Grid
                  item
                  xs={6}
                  sm={6}
                  sx={{
                    display: "flex !important",
                    justifyContent: "right !important;",
                  }}
                >
                  <StyledButton variant="contained" type="submit">
                    Next
                  </StyledButton>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Grid>
      </Grid>
    </StyledCard>
  );
};
